import {assert} from 'Asserts';
import {DecimalRoundingStrategy, decimalRoundingStrategyToDecimalJSRoundingMode} from 'util/Decimals';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

/**
 * Counts significant figures in a PreciseDecimal.
 *
 * Considers pure 0 as still having a significant figure.
 *
 * Note: All trailing zeros up to the decimal are considered significant in Standard Notation.
 * Example: 12300 is treated as having 5 significant figures
 * Represent in Scientific Notation as 1.23E+4 or 1.2300E+4 when trying to disambiguate significant figures.
 *
 * Keep in sync with benchling/lib/precise_decimal/significant_figures.py:count_sig_figs
 */
export function countSigFigs(preciseDecimal: PreciseDecimal): number {
  const scientificDecimal = preciseDecimal.toScientificNotation();
  const {standardDecimal, scientificCoefficient} =
    scientificDecimal.value.match(PreciseDecimal.PRECISE_DECIMAL_REGEX)?.groups ?? {};
  // fallback to the standard decimal to handle cases with 0.000...
  const flatSignificantDigits = (scientificCoefficient || standardDecimal || '').replace('.', '');
  return flatSignificantDigits.length;
}

/** Return the minimum number of significant figures across multiple PreciseDecimals.
 *
 * This is useful for determining the number of significant figures to apply after a calculation. Returns
 * undefined if no PreciseDecimals are provided.
 *
 * Keep in sync with benchling/lib/precise_decimal/significant_figures.py:find_min_sig_figs
 */
export function findMinSigFigs(...preciseDecimals: Array<PreciseDecimal>): number | undefined {
  if (preciseDecimals.length === 0) {
    return undefined;
  }
  return Math.min(...preciseDecimals.map((d) => countSigFigs(d)));
}

/**
 * Truncates or extends a PreciseDecimal to the specified number of significant figures.
 *
 * Allows specifying a custom rounding strategy or defaults to the IEEE-754 standard HALF_EVEN.
 *
 * This will try to return the decimal in the same notation as the input. Warning, if the Standard Notation
 * result would be ambiguous (like with 12300), then it will return the Scientific Notation version of the
 * decimal instead (1.230E+4).
 *
 * Keep in sync with benchling/lib/precise_decimal/significant_figures.py:apply_sig_figs
 */
export function applySigFigs(
  preciseDecimal: PreciseDecimal,
  sigFigs: number,
  roundingStrategy: DecimalRoundingStrategy = DecimalRoundingStrategy.ROUND_HALF_EVEN
): PreciseDecimal {
  assert(sigFigs > 0, 'sigFigs must be greater than 0', {sigFigs});
  const sigFigDecimal = preciseDecimal.decimal.toSignificantDigits(
    sigFigs,
    decimalRoundingStrategyToDecimalJSRoundingMode(roundingStrategy)
  );
  const scientificNotationPreciseDecimal = PreciseDecimal.of(sigFigDecimal.toExponential(sigFigs - 1));
  const inNotationPreciseDecimal = scientificNotationPreciseDecimal.toNotation(preciseDecimal.notation);
  // If the number of significant figures in the desired notation is different from the expected number of
  // significant figures, then we *must* return the scientific notation form to retain the exact precision.
  if (countSigFigs(scientificNotationPreciseDecimal) !== countSigFigs(inNotationPreciseDecimal)) {
    return scientificNotationPreciseDecimal;
  }
  return inNotationPreciseDecimal;
}
